import { Pencil2Icon, PlusIcon } from '@radix-ui/react-icons'
import { type CSSProperties } from 'react'
import {
	data,
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
	Link,
	useLoaderData,
} from 'react-router'
import { parseSafeZone, safeZoneImageStyle } from '#app/domains/program/pure'
import { prisma } from '#app/utils/db.server.js'
import { requireFestivalVisible } from '#app/utils/maintenance.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import performersStyleUrl from '../../styles/performers.css?url'
import { pageMeta } from '#app/utils/page-meta.ts'

export const links: LinksFunction = () => [
	{ rel: 'stylesheet', href: performersStyleUrl },
]

export const meta: MetaFunction = (args) =>
	pageMeta(args, {
		title: 'Artistane',
		description:
			'Bli kjend med dei som spelar på Huskonsertfestivalen på Vorrå.',
	})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireFestivalVisible(request)
	const performers = await prisma.performer.findMany({
		orderBy: { name: 'asc' },
		select: {
			id: true,
			slug: true,
			name: true,
			shortBio: true,
			imageUrl: true,
			imageSafeZone: true,
			_count: {
				select: {
					events: { where: { date: { gte: new Date() } } },
				},
			},
		},
	})
	return data({ performers })
}

/** "Kari Nordmann" → "KN" */
const initials = (name: string) =>
	name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join('')

type Performer = ReturnType<
	typeof useLoaderData<typeof loader>
>['performers'][number]

export default function PerformersIndex() {
	const { performers } = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const canEdit = user?.roles.some((role) =>
		role.permissions.some(
			(p) =>
				p.action === 'update' && p.entity === 'event' && p.access === 'any',
		),
	)
	// who's on this year first; the rest of the family after
	const playing = performers.filter((p) => p._count.events > 0)
	const earlier = performers.filter((p) => p._count.events === 0)

	return (
		<main className="pf">
			<header className="pf-head">
				<p className="pf-kicker">Artistane</p>
				<h1 className="pf-title">Folka på scenen</h1>
				<p className="pf-lede">Bli kjend med dei som opptrer på festivalen.</p>
				{canEdit ? (
					<Link to="/performers/new" className="pf-new">
						<PlusIcon aria-hidden="true" /> Opprett artist
					</Link>
				) : null}
			</header>

			{performers.length === 0 ? (
				<p className="pf-empty">Ingen artistar er registrerte enno.</p>
			) : (
				<>
					{playing.length ? (
						<PerformerSection
							title="Spelar i år"
							performers={playing}
							canEdit={Boolean(canEdit)}
						/>
					) : null}
					{earlier.length ? (
						<PerformerSection
							title={playing.length ? 'Tidlegare år' : 'Artistar'}
							performers={earlier}
							canEdit={Boolean(canEdit)}
						/>
					) : null}
				</>
			)}
		</main>
	)
}

function PerformerSection({
	title,
	performers,
	canEdit,
}: {
	title: string
	performers: Performer[]
	canEdit: boolean
}) {
	return (
		<section className="pf-section" aria-label={title}>
			<h2 className="pf-h2">
				{title} <span className="pf-count">{performers.length}</span>
			</h2>
			<ul className="pf-grid">
				{performers.map((performer, i) => (
					<li
						key={performer.id}
						className="pf-card"
						style={{ '--pf-i': i } as CSSProperties}
					>
						<Link
							to={`/performers/${performer.slug}`}
							viewTransition
							className="pf-link"
						>
							<span className="pf-portrait">
								{performer.imageUrl ? (
									<img
										src={performer.imageUrl}
										alt=""
										loading="lazy"
										decoding="async"
										style={{
											viewTransitionName: `performer-photo-${performer.slug}`,
											...safeZoneImageStyle(
												parseSafeZone(performer.imageSafeZone),
												1,
											),
										}}
									/>
								) : (
									<span className="pf-monogram" aria-hidden="true">
										{initials(performer.name)}
									</span>
								)}
							</span>
							<span className="pf-text">
								<span
									className="pf-name"
									style={{ viewTransitionName: `performer-${performer.slug}` }}
								>
									{performer.name}
								</span>
								{performer.shortBio ? (
									<span className="pf-bio">{performer.shortBio}</span>
								) : null}
								{performer._count.events > 0 ? (
									<span className="pf-gigs">
										{performer._count.events === 1
											? '1 konsert i år'
											: `${performer._count.events} konsertar i år`}
									</span>
								) : null}
							</span>
						</Link>
						{canEdit ? (
							<Link
								to={`/performers/${performer.slug}/edit`}
								className="pf-edit"
								aria-label={`Rediger ${performer.name}`}
							>
								<Pencil2Icon aria-hidden="true" />
							</Link>
						) : null}
					</li>
				))}
			</ul>
		</section>
	)
}
